import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, LogOut, User as UserIcon, Languages, Trophy, Swords, BarChart3, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const PING_MS = 20000;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    const ping = () => api.post("/presence/ping").catch((e) => console.warn("presence ping failed:", e));
    ping();
    const iv = setInterval(ping, PING_MS);
    return () => clearInterval(iv);
  }, [userId]);
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-base/95 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-3 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <Activity className="w-6 h-6 text-signal" strokeWidth={2.5} />
          <span className="font-heading font-black text-xl tracking-tight hidden sm:inline">SNETCH</span>
        </Link>

        <nav className="flex items-center gap-2 md:gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 text-xs font-mono uppercase text-white/60 hover:text-white transition-colors px-2 py-1"
          >
            <Languages className="w-4 h-4" />
            {lang}
          </button>

          {user ? (
            <>
              {typeof user.wallet === "number" && (
                <Link to="/profile" data-testid="wallet-balance" className="font-mono text-sm text-signal border border-signal/30 hover:bg-signal/10 transition-colors rounded-sm px-2.5 py-1 tabular-nums">
                  €{user.wallet.toFixed(2)}
                </Link>
              )}
              <Link to="/lobby" data-testid="nav-lobby">
                <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/5 gap-1.5 px-2 md:px-4">
                  <Swords className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav_lobby")}</span>
                </Button>
              </Link>
              <Link to="/leaderboard" data-testid="nav-ranking">
                <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/5 gap-1.5 px-2 md:px-4">
                  <Trophy className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav_ranking")}</span>
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="nav-profile-menu" variant="ghost" size="icon" className="text-white/80 hover:bg-white/5 gap-1">
                    <UserIcon className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-panel border-white/10 text-white">
                  <div data-testid="menu-user-header" className="px-2 py-1.5 flex items-center justify-between gap-3">
                    <span className="font-mono text-xs text-white/50 truncate max-w-[160px]">{user.email}</span>
                    {user.role === "admin" && (
                      <span data-testid="menu-admin-badge" className="font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-signal text-black">admin</span>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")} className="cursor-pointer focus:bg-white/10">
                    <UserIcon className="w-4 h-4 mr-2" /> {t("menu_profile")}
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuItem data-testid="menu-admin-kpi" onClick={() => navigate("/admin/kpi")} className="cursor-pointer focus:bg-white/10">
                        <BarChart3 className="w-4 h-4 mr-2" /> {t("menu_kpi")}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-admin-docs" onClick={() => navigate("/download-docs")} className="cursor-pointer focus:bg-white/10">
                        <FileText className="w-4 h-4 mr-2" /> {t("menu_docs")}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer text-loss focus:bg-loss/10 focus:text-loss">
                    <LogOut className="w-4 h-4 mr-2" /> {t("menu_logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/auth" data-testid="nav-login">
                <Button variant="ghost" className="text-white/80 hover:bg-white/5">{t("nav_login")}</Button>
              </Link>
              <Link to="/auth?mode=register" data-testid="nav-register">
                <Button className="bg-signal text-black hover:bg-signal/90 rounded-sm font-semibold">
                  {t("nav_register")}
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
